import { Injectable, inject } from '@angular/core';
import { createEffect, ofType } from '@ngneat/effects';
import { forkJoin, map, of, switchMap, tap } from 'rxjs';
import { StopActions } from '@data/stop/stop.actions';
import { StopService } from '@data/stop/stop.service';
import { StopStore } from '@data/stop/stop.store';
import { DELETE_OK } from '@data/entity-crud.service';
import { HttpUtils } from '@shared/util/http.util';
import { NotificationService } from '@core/services/notification/notification.service';

/**
 * Every request is caught before it reaches the action stream: an error thrown out of an
 * effect's inner observable tears the whole stream down, so one failed request (an expired
 * token, a hiccup) used to leave the organizer's exhibitor table empty and spinning forever,
 * with every later Stop action silently dead as well — and nothing on screen to explain it.
 */
@Injectable({ providedIn: 'root' })
export class StopEffects {
  private stopStore = inject(StopStore);
  private stopService = inject(StopService);
  private httpUtils = inject(HttpUtils);
  private notify = inject(NotificationService);

  load = createEffect((actions) =>
    actions.pipe(
      ofType(StopActions.load),
      switchMap((request) => {
        this.stopStore.setLoading(true);
        return this.stopService
          .listForSession(request.id)
          .pipe(this.httpUtils.catchAndPrintError());
      }),
      tap((stops) => {
        if (stops) this.stopStore.set(stops);
        else this.notify.error('ERRORS.STOP.load');
        this.stopStore.setLoading(false);
      }),
    ),
  );

  create = createEffect((actions) =>
    actions.pipe(
      ofType(StopActions.create),
      switchMap((request) =>
        this.stopService.create(request.input).pipe(this.httpUtils.catchAndPrintError()),
      ),
      tap((stop) => {
        if (stop) this.stopStore.add(stop);
        else this.notify.error('ERRORS.STOP.save');
      }),
    ),
  );

  update = createEffect((actions) =>
    actions.pipe(
      ofType(StopActions.update),
      switchMap((request) =>
        this.stopService.update(request.targetId, request.input).pipe(
          map(() => true),
          this.httpUtils.catchAndPrintError(),
          tap((ok) => {
            if (ok) this.stopStore.update(request.targetId, { ...request.input });
            else this.notify.error('ERRORS.STOP.save');
          }),
        ),
      ),
    ),
  );

  delete = createEffect((actions) =>
    actions.pipe(
      ofType(StopActions.deleteStop),
      switchMap((request) =>
        this.stopService.delete(request.targetId).pipe(
          this.httpUtils.catchAndPrintError(),
          tap((response) => {
            if (response?.status === DELETE_OK) this.stopStore.remove(request.targetId);
            else this.notify.error('ERRORS.STOP.delete');
          }),
        ),
      ),
    ),
  );

  deleteMany = createEffect((actions) =>
    actions.pipe(
      ofType(StopActions.deleteMany),
      switchMap((request) => {
        if (!request.stops.length) return of([]);
        return forkJoin(
          request.stops.map((stop) =>
            this.stopService.delete(stop.id).pipe(
              this.httpUtils.catchAndPrintError(),
              tap((response) => {
                if (response?.status === DELETE_OK) this.stopStore.remove(stop.id);
                else this.notify.error('ERRORS.STOP.delete');
              }),
            ),
          ),
        );
      }),
    ),
  );
}
